import React, { useEffect, useState } from "react";
import { api, TASK_CLASS_LABELS, DAY_TYPE_LABELS, DAY_TYPE_COLORS } from "@/lib/api";
import { toast } from "sonner";
import { ChevronRight } from "lucide-react";

const SL = { effort: "Effort", rest: "Rest", sharpness: "Sharpness", readiness: "Readiness" };
const cell = (v) => {
  const mag = Math.min(Math.abs(v) / 12, 1);
  if (v > 0.3) return { background: `rgba(22,163,74,${0.12 + mag * 0.55})` };
  if (v < -0.3) return { background: `rgba(220,38,38,${0.12 + mag * 0.55})` };
  return {};
};

function DayTypeBreakdown({ activity }) {
  const groups = {};
  (activity.instances || []).forEach((it) => { (groups[it.day_type] = groups[it.day_type] || []).push(it); });
  const order = Object.keys(DAY_TYPE_LABELS).filter((dt) => groups[dt]);
  return (
    <div className="bg-surface border-t border-border p-4" data-testid={`act-breakdown-${activity.title}`}>
      <div className="flex flex-wrap gap-2 mb-3">
        {order.map((dt) => (
          <span key={dt} className="text-xs px-2 py-1 border border-border bg-background flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ background: DAY_TYPE_COLORS[dt] }} />
            {DAY_TYPE_LABELS[dt]} <b className="font-heading">{groups[dt].length}</b>
          </span>
        ))}
      </div>
      <div className="space-y-3">
        {order.map((dt) => (
          <div key={dt}>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground mb-1.5" style={{ color: DAY_TYPE_COLORS[dt] }}>{DAY_TYPE_LABELS[dt]}</div>
            <div className="border border-border divide-y divide-border bg-background">
              {groups[dt].map((it, i) => (
                <div key={i} className="p-2 flex items-center gap-3 text-xs" data-testid={`inst-${dt}-${i}`}>
                  <span className="text-muted-foreground w-20 tabular-nums">{it.date || "-"}</span>
                  <span className="w-16">{it.duration_min ? `${it.duration_min} min` : "-"}</span>
                  <span className="w-16 text-muted-foreground">{it.intensity || "-"}</span>
                  <span className="flex-1 text-muted-foreground">
                    {it.metrics && Object.keys(it.metrics).length > 0 ? Object.entries(it.metrics).map(([k, v]) => `${k}:${v}`).join(" · ") : ""}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AthleteTaskLibrary({ athleteId, embedded }) {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const [tod, setTod] = useState({});   // task_class -> {hour, impact_on_next_rest}
  const load = () => { if (athleteId) api.get(`/athletes/${athleteId}/task-library`).then((r) => setD(r.data)); };
  useEffect(load, [athleteId]);
  useEffect(() => {
    if (!athleteId) return;
    api.get(`/athletes/${athleteId}/time-insight`).then((r) => {
      const per = {};
      for (const h of (r.data.hours || [])) {
        const cls = h.top_class;
        if (!cls || !h.eligible || h.impact_on_next_rest == null) continue;
        const cur = per[cls];
        if (!cur || h.impact_on_next_rest > cur.impact_on_next_rest) per[cls] = h;
      }
      setTod(per);
    }).catch(() => setTod({}));
  }, [athleteId]);
  const prune = async () => {
    try { const r = await api.post(`/athletes/${athleteId}/task-library/prune`); toast.success(`Pruned ${r.data.activities_pruned} inactive activities (${r.data.tasks_deleted} tasks)`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Prune failed"); }
  };
  if (!d) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  const hasExpired = d.activities.some((a) => a.status === "expired");
  const STATUS_BADGE = {
    calculating: { label: "Calculating", cls: "border-border text-muted-foreground" },
    recalibrate: { label: "Recalibrate", cls: "border-amber-500 text-amber-600" },
    expired: { label: "Inactive 90d+", cls: "border-traffic-red text-traffic-red" },
  };

  const fmtHour = (h) => (h == null ? "-" : `${String(h).padStart(2, "0")}:00`);

  const ClassRow = ({ label, impact, cls, testid }) => {
    const t = tod[cls];
    return (
      <tr className="border-b border-border last:border-0" data-testid={testid}>
        <td className="p-3 font-medium">{label}</td>
        {d.scores.map((s) => <td key={s} className="p-3 text-right tabular-nums font-medium" style={cell(impact[s])}>{impact[s] > 0 ? "+" : ""}{impact[s]}</td>)}
        <td className="p-3 text-right tabular-nums font-heading font-bold" data-testid={`cls-tod-${cls}`}>
          {t ? fmtHour(t.hour) : <span className="text-muted-foreground italic">N/A</span>}
          {t && <span className={`ml-1 text-[10px] ${t.impact_on_next_rest >= 0 ? "text-traffic-green" : "text-traffic-red"}`}>{t.impact_on_next_rest > 0 ? "+" : ""}{t.impact_on_next_rest}</span>}
        </td>
      </tr>
    );
  };

  return (
    <div className={embedded ? "" : "p-8"}>
      <p className="text-xs tracking-[0.3em] uppercase font-bold text-muted-foreground mb-2">Activity impact library</p>
      <p className="text-sm text-muted-foreground max-w-2xl mb-4">Impact scores are derived from <b>{d.athlete}'s own</b> activity log · how much each activity/type moves each score per session. An activity needs <b>more than {d.min_completions} completions in 90 days</b> before its impact is trusted (shown as <b>Calculating</b> until then). <b>Click any activity</b> to see every time it was done.</p>
      {hasExpired && (
        <button data-testid="prune-inactive-btn" onClick={prune} className="mb-6 text-xs border border-traffic-red text-traffic-red px-3 py-1.5 hover:bg-traffic-red/10 transition-colors">Prune inactive activities (never qualified, 90d+ idle)</button>
      )}

      <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3">By activity (granular) · with optimal training time</p>
      <div className="border border-border overflow-x-auto mb-8">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">
            <th className="text-left p-3">Activity</th>{d.scores.map((s) => <th key={s} className="text-right p-3">{SL[s]}</th>)}
            <th className="text-right p-3">Optimal time</th>
          </tr></thead>
          <tbody>
            {d.activities.length === 0 ? <tr><td className="p-4 text-muted-foreground" colSpan={d.scores.length + 2}>No logged activities yet.</td></tr> :
              d.activities.map((a, i) => {
                const badge = STATUS_BADGE[a.status];
                const t = tod[a.task_class];
                return (
                <React.Fragment key={i}>
                  <tr onClick={() => setOpen(open === i ? null : i)} className="border-b border-border last:border-0 cursor-pointer hover:bg-surface" data-testid={`act-row-${i}`}>
                    <td className="p-3 font-medium">
                      <span className="inline-flex items-center gap-2 flex-wrap">
                        <ChevronRight className={`w-4 h-4 transition-transform ${open === i ? "rotate-90" : ""}`} />
                        {a.title}
                        <span className="text-xs text-muted-foreground font-normal ml-1">{TASK_CLASS_LABELS[a.task_class]} · ×{a.count}</span>
                        {badge && <span data-testid={`act-status-${i}`} title={a.note || ""} className={`text-[9px] px-1.5 py-0.5 border font-bold uppercase tracking-wider ${badge.cls}`}>{badge.label}</span>}
                      </span>
                    </td>
                    {a.eligible
                      ? d.scores.map((s) => <td key={s} className="p-3 text-right tabular-nums font-medium" style={cell(a.impact[s])}>{a.impact[s] > 0 ? "+" : ""}{a.impact[s]}</td>)
                      : <td colSpan={d.scores.length} className="p-3 text-right text-xs text-muted-foreground italic" data-testid={`act-calculating-${i}`}>{a.note || "Calculating…"}</td>}
                    <td className="p-3 text-right tabular-nums font-heading font-bold" data-testid={`act-tod-${i}`}>
                      {t ? fmtHour(t.hour) : <span className="text-muted-foreground italic">N/A</span>}
                    </td>
                  </tr>
                  {open === i && (
                    <tr><td colSpan={d.scores.length + 2} className="p-0"><DayTypeBreakdown activity={a} /></td></tr>
                  )}
                </React.Fragment>
                );
              })}
          </tbody>
        </table>
      </div>

      <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3">By class · with optimal time</p>
      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">
            <th className="text-left p-3">Type</th>{d.scores.map((s) => <th key={s} className="text-right p-3">{SL[s]}</th>)}
            <th className="text-right p-3">Optimal time</th>
          </tr></thead>
          <tbody>
            {Object.entries(d.class_impact).map(([c, imp]) => <ClassRow key={c} testid={`cls-row-${c}`} label={TASK_CLASS_LABELS[c]} impact={imp} cls={c} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}
